import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, Boxes, DatabaseBackup, FileText, Filter, Flame, Gauge, Info, KeyRound, LayoutDashboard, Network, Pencil, Play, Plus, Power, PowerOff, Radar, RefreshCw, RotateCcw, Search, Settings2, ShieldAlert, Shuffle, Signal, Split, Stethoscope, Trash2, Users, X } from 'lucide-react';
import type { AddressEntry, HealthState, Resource, StorageTarget, VpnLink } from '@itr/shared';
import { ResourceVpnCard, VpnLinkEditor } from '../components/VpnLinks';
import {
  accessHostFor,
  accessUsername,
  canHostLoadBalancer,
  formatBytes,
  formatRelative,
  formatMetric,
  HEALTH_STATES,
  isAsterisk,
  isCameraResource,
  isDinstar,
  isMagnusBilling,
  isMikrotik,
  isYeastar,
  isValidMac,
  isValidTarget,
  macVendor,
  securityPlatformOf,
} from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useRealtimeEvent } from '../api/realtime';
import { useLiveResources } from '../api/liveResources';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  autoIntent,
  bulkResultMessage,
  runBulk,
  useDebounced,
  useFetch,
  useSelection,
  useToast,
  type Selection,
  ReachDot,
} from '../components/ui';
import { TimeseriesChart } from '../components/charts';
import { SystemResources, VITAL_METRICS } from '../components/SystemResources';
import {
  ResourceAccessCard,
  ResourceConfigCard,
  ResourceDiagnosticsCard,
  ResourceDiscoveryCard,
  ResourceServicesCard,
  ResourceSystemServicesCard,
  ResourceMikrotikUsersCard,
  ResourceTorchCard,
  ResourceVideoCard,
} from '../components/ResourceTools';
import { ResourceSecurityCard } from '../components/ResourceSecurity';
import { AiChatPanel } from '../components/AiChatPanel';
import { EscrowBadge, IgnoreBulkButtons, IgnoreStatusButton, IgnoredBadge, NoticeList, ResourceHealth } from '../components/ResourceHealth';
import { WatchToggle } from '../notifications/WatchToggle';
import { GoipRebootButton } from './GoipMonitor';
import { DownloadRunButton, PlanForm, RestoreDialog, RunLogButton, RunLogDrawer, type PlanRow, type RunRow } from './Backups';

/** Only the fields the resource screens need off `/api/customers`. */
interface CustomerOption {
  id: string;
  name: string;
}

interface SiteOption {
  id: string;
  customerId: string;
  name: string;
}

/**
 * Customer + site pickers, wired together: picking a client narrows the site
 * list, and picking a site pins the client, mirroring the server rule that a
 * resource's tenant is derived from its site.
 */
function PlacementFields({
  customerId,
  siteId,
  onChange,
}: {
  customerId: string;
  siteId: string;
  onChange: (next: { customerId: string; siteId: string }) => void;
}) {
  const { data: customers } = useFetch(() => api.get<CustomerOption[]>('/api/customers'), []);
  const { data: sites } = useFetch(
    () => api.get<SiteOption[]>('/api/sites', customerId ? { customerId } : undefined),
    [customerId],
  );

  return (
    <>
      <div className="field">
        <label className="field-label">Client</label>
        <select
          className="select"
          value={customerId}
          onChange={(e) => onChange({ customerId: e.target.value, siteId: '' })}
        >
          <option value="">Internal / shared</option>
          {(customers ?? []).map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>
        <span className="field-help">Assigning a client scopes this resource to their portal view.</span>
      </div>

      <div className="field">
        <label className="field-label">Site</label>
        <select
          className="select"
          value={siteId}
          disabled={!customerId}
          onChange={(e) => onChange({ customerId, siteId: e.target.value })}
        >
          <option value="">{customerId ? 'No specific site' : 'Pick a client first'}</option>
          {(sites ?? []).map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </select>
        <span className="field-help">
          {customerId && (sites ?? []).length === 0
            ? 'This client has no sites yet — add one from their page.'
            : 'The location this resource physically lives at.'}
        </span>
      </div>
    </>
  );
}

interface ResourceKind {
  kind: string;
  label: string;
  pluginId: string;
  pluginName: string;
  configFields: Array<{
    key: string;
    label: string;
    type: string;
    required?: boolean;
    default?: unknown;
    placeholder?: string;
    help?: string;
    choices?: Array<{ value: string; label: string }>;
    secret?: boolean;
  }>;
}

/* ================================================================== */
/* List                                                               */
/* ================================================================== */

export function ResourcesPage({ pluginFilter }: { pluginFilter?: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [health, setHealth] = useState<string>(params.get('health') ?? '');
  const [addOpen, setAddOpen] = useState(false);
  const debouncedSearch = useDebounced(search, 280);

  // Driven by the URL so "N attached" links from the customer list land here
  // already filtered, and the filtered view stays shareable.
  const customerId = params.get('customerId') ?? '';
  const { data: customers } = useFetch(() => api.get<CustomerOption[]>('/api/customers'), []);
  const { data: sites } = useFetch(() => api.get<SiteOption[]>('/api/sites'), []);
  const customerName = useMemo(
    () => new Map((customers ?? []).map((c) => [c.id, c.name])),
    [customers],
  );
  const siteName = useMemo(() => new Map((sites ?? []).map((s) => [s.id, s.name])), [sites]);

  const query = useMemo(
    () => ({
      pluginId: pluginFilter,
      search: debouncedSearch || undefined,
      health: health || undefined,
      customerId: customerId || undefined,
      limit: 300,
    }),
    [pluginFilter, debouncedSearch, health, customerId],
  );

  const { data, loading, error, reload } = useFetch(() => api.get<Resource[]>('/api/resources', query), [
    JSON.stringify(query),
  ]);

  // Filters live server-side, so any add/edit/delete anywhere may change
  // what belongs in this list.
  const resources = useLiveResources(data, reload, { all: true });

  const counts = useMemo(() => {
    const out: Record<string, number> = { up: 0, degraded: 0, down: 0, unknown: 0, maintenance: 0 };
    for (const r of resources) out[r.health] = (out[r.health] ?? 0) + 1;
    return out;
  }, [resources]);

  const canWrite = can('resource:write');
  const selection = useSelection(resources.map((r) => r.id));

  const bulkSetEnabled = async (enabled: boolean) => {
    const result = await runBulk(selection.selectedIds, (id) => api.patch(`/api/resources/${id}`, { enabled }));
    const { message, intent } = bulkResultMessage(enabled ? 'Enabled' : 'Disabled', result);
    toast(message, intent);
    selection.clear();
    reload();
  };

  const bulkDelete = async () => {
    if (!confirm(`Delete ${selection.count} resource(s)? Their history and metrics go with them.`)) return;
    const result = await runBulk(selection.selectedIds, (id) => api.delete(`/api/resources/${id}`));
    const { message, intent } = bulkResultMessage('Deleted', result);
    toast(message, intent);
    selection.clear();
    reload();
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">{pluginFilter ? titleCase(pluginFilter) : 'All resources'}</h1>
          <div className="page-subtitle">
            {resources.length} monitored · {counts.down} down · {counts.degraded} degraded
          </div>
        </div>
        <div className="row">
          <button className="btn btn-icon" onClick={reload} title="Refresh" aria-label="Refresh">
            <RefreshCw size={15} />
          </button>
          {can('resource:write') && (
            <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
              <Plus size={15} /> Add resource
            </button>
          )}
        </div>
      </div>

      <div className="row" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
          <Search
            size={15}
            style={{ position: 'absolute', left: 10, top: '50%', translate: '0 -50%', color: 'var(--itr-text-subtle)' }}
          />
          <input
            className="input"
            style={{ paddingLeft: 32 }}
            placeholder="Search by name or address…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setParams((p) => {
                const next = new URLSearchParams(p);
                if (e.target.value) next.set('search', e.target.value);
                else next.delete('search');
                return next;
              });
            }}
          />
        </div>

        <div className="row" style={{ gap: 4 }}>
          <Filter size={14} style={{ color: 'var(--itr-text-subtle)' }} />
          <button className={`btn btn-sm${health === '' ? ' btn-primary' : ''}`} onClick={() => setHealth('')}>
            All
          </button>
          {HEALTH_STATES.filter((h) => h !== 'maintenance').map((state) => (
            <button
              key={state}
              className={`btn btn-sm${health === state ? ' btn-primary' : ''}`}
              onClick={() => setHealth(health === state ? '' : state)}
              style={{ textTransform: 'capitalize' }}
            >
              {state} {counts[state] ? `(${counts[state]})` : ''}
            </button>
          ))}
        </div>

        {(customers ?? []).length > 0 && (
          <select
            className="select"
            style={{ width: 'auto', minWidth: 170 }}
            value={customerId}
            onChange={(e) => {
              setParams((p) => {
                const next = new URLSearchParams(p);
                if (e.target.value) next.set('customerId', e.target.value);
                else next.delete('customerId');
                return next;
              });
            }}
            aria-label="Filter by customer"
          >
            <option value="">All customers</option>
            {(customers ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {canWrite && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm" onClick={() => bulkSetEnabled(true)}>
            <Power size={12} /> Enable
          </button>
          <button className="btn btn-sm" onClick={() => bulkSetEnabled(false)}>
            <PowerOff size={12} /> Disable
          </button>
          <IgnoreBulkButtons selection={selection} onChanged={reload} />
          <button className="btn btn-sm btn-danger" onClick={bulkDelete}>
            <Trash2 size={12} /> Delete
          </button>
        </SelectionBar>
      )}

      <div className="card">
        {loading && !data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={8} cols={5} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : resources.length === 0 ? (
          <EmptyState
            icon={<Boxes size={20} />}
            title={search || health || customerId ? 'No matching resources' : 'Nothing monitored yet'}
            text={
              search || health || customerId
                ? 'Try clearing the filters.'
                : 'Add a firewall, server, internet line or VOIP system to start collecting.'
            }
            action={
              can('resource:write') && !search && !health && !customerId ? (
                <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
                  <Plus size={15} /> Add resource
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canWrite && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th>Name</th>
                  <th style={{ width: 110 }}>Health</th>
                  <th style={{ width: 130 }}>Kind</th>
                  <th style={{ width: 160 }}>Client / site</th>
                  <th>Status</th>
                  <th style={{ width: 140 }}>Address</th>
                  <th data-align="right" style={{ width: 130 }}>
                    Last check
                  </th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {resources.map((resource) => (
                  <ResourceRow
                    key={resource.id}
                    resource={resource}
                    customerName={resource.customerId ? customerName.get(resource.customerId) : undefined}
                    siteName={resource.siteId ? siteName.get(resource.siteId) : undefined}
                    selection={canWrite ? selection : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title="Add a resource">
        <AddResourceForm
          onCreated={() => {
            setAddOpen(false);
            reload();
            toast('Resource added — first check runs shortly', 'good');
          }}
        />
      </Drawer>
    </>
  );
}

function ResourceRow({
  resource,
  customerName,
  siteName,
  selection,
}: {
  resource: Resource;
  customerName?: string;
  siteName?: string;
  /** Omitted entirely (no checkbox cell) when the caller lacks resource:write. */
  selection?: Selection;
}) {
  const navigate = useNavigate();
  return (
    <tr data-clickable="true" onClick={() => navigate(`/resources/${resource.id}`)}>
      {selection && (
        <td>
          <RowCheckbox selection={selection} id={resource.id} />
        </td>
      )}
      <td>
        <div style={{ fontWeight: 550 }}>
          <ReachDot health={resource.health} />
          {resource.name}
        </div>
        {resource.tags.length > 0 && (
          <div className="row" style={{ gap: 4, marginTop: 3 }}>
            {resource.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="badge" style={{ height: 17, fontSize: 10.5 }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </td>
      <td>
        <ResourceHealth resource={resource} />
        <IgnoredBadge resource={resource} />
        <EscrowBadge resource={resource} />
      </td>
      <td>
        <Badge>{resource.kind}</Badge>
      </td>
      <td className="truncate" style={{ maxWidth: 160, fontSize: 12.5 }}>
        {customerName ?? <span className="subtle">Internal</span>}
        {siteName && (
          <div className="subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
            {siteName}
          </div>
        )}
      </td>
      <td className="subtle truncate" style={{ maxWidth: 320, fontSize: 12.5 }}>
        {resource.statusMessage ?? '—'}
      </td>
      <td className="mono subtle">{accessHostFor(resource) ?? '—'}</td>
      <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
        {resource.lastCheckedAt ? formatRelative(resource.lastCheckedAt) : 'never'}
      </td>
      <td>
        <WatchToggle scope="resource" id={resource.id} name={resource.name} size="sm" />
      </td>
    </tr>
  );
}

/* ================================================================== */
/* Detail                                                             */
/* ================================================================== */

interface ResourceDetailData extends Resource {
  children: Resource[];
  parent: { id: string; name: string; kind: string; health: string } | null;
  metrics: string[];
  uptime24h: number;
  uptime30d: number;
  openAlerts: Array<{ id: string; title: string; severity: string; lastSeenAt: number }>;
}

type DetailTab = 'dashboard' | 'security' | 'services' | 'access' | 'diagnostics' | 'discovery' | 'config' | 'users' | 'torch' | 'backups' | 'details';

export function ResourceDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [range, setRange] = useState(6);
  // The tab lives in the address (?tab=security), so a reload, a link from an
  // alert or the back button lands on the same one.
  const [searchParams, setSearchParams] = useSearchParams();
  // 'tools' is the old name of the group now split into Access, Diagnostics and the MikroTik tabs.
  const requestedTab = ((searchParams.get('tab') ?? 'dashboard') === 'tools' ? 'access' : (searchParams.get('tab') ?? 'dashboard')) as DetailTab;
  const setTab = (next: DetailTab) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'dashboard') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );

  const { data: fetched, loading, error, reload } = useFetch(
    () => api.get<ResourceDetailData>(`/api/resources/${id}`),
    [id],
    { refreshMs: 30_000 },
  );
  // Each collection's reading, applied the moment its realtime frame lands —
  // the status, its reason and "last check" should not wait for a full page
  // refetch (uptime, alerts, metrics) to round-trip. A fresh fetch supersedes it.
  const [live, setLive] = useState<{ id: string; health: HealthState; statusMessage: string | null; lastCheckedAt: number; escrow?: unknown } | null>(null);
  useEffect(() => setLive(null), [fetched]);
  const data = useMemo(
    () =>
      fetched && live && live.id === fetched.id
        ? {
            ...fetched,
            health: live.health,
            statusMessage: live.statusMessage,
            lastCheckedAt: live.lastCheckedAt,
            ...('escrow' in live ? { attributes: { ...fetched.attributes, escrow: live.escrow } } : {}),
          }
        : fetched,
    [fetched, live],
  );
  const { data: customers } = useFetch(() => api.get<CustomerOption[]>('/api/customers'), []);
  const { data: sites } = useFetch(() => api.get<SiteOption[]>('/api/sites'), []);
  const owner = data?.customerId ? (customers ?? []).find((c) => c.id === data.customerId) : undefined;
  const site = data?.siteId ? (sites ?? []).find((s) => s.id === data.siteId) : undefined;

  // Someone else deleting it (another tab, a teammate, a bulk delete on the
  // map) leaves this page showing a ghost; say so instead.
  const [deletedElsewhere, setDeletedElsewhere] = useState(false);
  useEffect(() => setDeletedElsewhere(false), [id]);
  useRealtimeEvent([`resources:${id}`, 'resources', 'sites'], (event) => {
    if (event.type === 'resource.updated' || event.type === 'resource.changed') {
      // Its own readings and edits, a parent renamed (the breadcrumb shows
      // it), and a child whose health actually flipped — not every child
      // check, which would refetch this page every few seconds.
      const child = data?.children.some((c) => c.id === event.resourceId);
      if (event.type === 'resource.updated' && event.resourceId === id) {
        setLive({
          id,
          health: event.health as HealthState,
          statusMessage: event.statusMessage,
          lastCheckedAt: event.ts,
          ...('escrow' in (event.attributes ?? {}) ? { escrow: event.attributes.escrow } : {}),
        });
        // Uptime, alerts and history only move when the state does.
        if (event.health !== event.previousHealth) reload();
      } else if (event.resourceId === id || event.resourceId === data?.parent?.id) reload();
      else if (child && (event.type === 'resource.changed' || event.health !== event.previousHealth)) reload();
    } else if (event.type === 'resource.deleted') {
      if (event.resourceId === id) setDeletedElsewhere(true);
      else if (data?.children.some((c) => c.id === event.resourceId) || event.resourceId === data?.parent?.id) reload();
    } else if (event.type === 'resource.created') {
      // A newly discovered device may have been placed under this one.
      if (event.customerId === (data?.customerId ?? null)) reload();
    } else if (event.type === 'site.changed' && event.siteId === data?.siteId) {
      reload();
    }
  });

  const checkNow = async () => {
    setChecking(true);
    try {
      await api.post(`/api/resources/${id}/check`);
      reload();
      toast('Check complete', 'good');
    } catch (err) {
      toast('Check failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setChecking(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Delete "${data?.name}"? Its history and metrics go with it.`)) return;
    await api.delete(`/api/resources/${id}`);
    toast('Resource deleted', 'good');
    // Return to wherever the user opened it from (a client, a site, a filtered
    // list). On a deep link there is nothing to go back to, so fall back to the
    // resource's own place in the hierarchy rather than the global list.
    if (location.key !== 'default') navigate(-1);
    else if (data?.customerId && data.siteId) navigate(`/clients/${data.customerId}/sites/${data.siteId}`, { replace: true });
    else if (data?.customerId) navigate(`/clients/${data.customerId}`, { replace: true });
    else navigate('/resources', { replace: true });
  };

  if (deletedElsewhere) {
    return <EmptyState title="This resource was deleted" text="It was removed while you had it open." action={<Link className="btn" to="/resources">All resources</Link>} />;
  }
  if (loading && !data) return <SkeletonTable rows={6} cols={3} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Resource not found" />;

  const tabs: Array<{ id: DetailTab; label: string; icon: typeof Activity; show: boolean }> = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { id: 'security', label: 'Security', icon: ShieldAlert, show: can('resource:read') && securityPlatformOf(data) !== null },
    { id: 'services', label: 'Services & ports', icon: Radar, show: can('tools:scan') },
    { id: 'access', label: 'Access', icon: KeyRound, show: can('tools:run') },
    { id: 'diagnostics', label: 'Diagnostics', icon: Stethoscope, show: can('tools:run') },
    { id: 'discovery', label: 'Discovery', icon: Search, show: can('tools:run') && isMikrotik(data) },
    { id: 'config', label: 'Configuration', icon: FileText, show: can('tools:run') && isMikrotik(data) },
    { id: 'users', label: 'Users', icon: Users, show: can('tools:run') && isMikrotik(data) },
    { id: 'torch', label: 'Torch', icon: Flame, show: can('tools:run') && isMikrotik(data) },
    { id: 'backups', label: 'Backups', icon: DatabaseBackup, show: can('backup:read') },
    { id: 'details', label: 'Details', icon: Info, show: true },
  ];
  // A link to a tab this resource or user does not get lands on the dashboard.
  const tab = tabs.some((t) => t.id === requestedTab && t.show) ? requestedTab : 'dashboard';

  return (
    <div className="content-narrow" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <div className="grow">
          {(owner || data.parent) && (
            <div className="page-subtitle" style={{ marginBottom: 2 }}>
              {owner && (
                <>
                  <Link to={`/clients/${owner.id}`}>{owner.name}</Link>
                  {site && (
                    <>
                      {' / '}
                      <Link to={`/clients/${owner.id}/sites/${site.id}`}>{site.name}</Link>
                    </>
                  )}
                </>
              )}
              {data.parent && (
                <>
                  {owner && ' / '}
                  <Link to={`/resources/${data.parent.id}`}>
                    <ReachDot health={data.parent.health} />
                    {data.parent.name}
                  </Link>
                </>
              )}
            </div>
          )}
          <div className="row" style={{ gap: 10 }}>
            <h1 className="page-title">
              <ReachDot health={data.health} size={10} />
              {data.name}
            </h1>
            <ResourceHealth resource={data} onChanged={reload} />
            <EscrowBadge resource={data} />
            <IgnoreStatusButton resource={data} onChanged={reload} />
          </div>
          <div className="page-subtitle">
            {data.kind} · {data.pluginId}
            {accessHostFor(data) && ` · ${accessHostFor(data)}`}
          </div>
        </div>
        <div className="row">
          {data.kind === 'vicidial-dialer' && (
            <Link className="btn" to={`/resources/${data.id}/monitor`}>
              <Gauge size={15} /> Server Monitor
            </Link>
          )}
          {data.kind === 'magnusbilling' && (
            <Link className="btn" to={`/resources/${data.id}/magnus`}>
              <Activity size={15} /> Live
            </Link>
          )}
          {data.kind === 'load-balancer' && (
            <Link className="btn" to={`/resources/${data.id}/balancer`}>
              <Split size={15} /> Load Balancing
            </Link>
          )}
          {canHostLoadBalancer(data) && (
            <Link className="btn" to={`/resources/${data.id}/balancer/install`}>
              <Split size={15} /> Install Load Balancing
            </Link>
          )}
          {data.kind === 'goip-gateway' && (
            <>
              <Link className="btn" to={`/resources/${data.id}/lines`}>
                <Signal size={15} /> GSM Lines
              </Link>
              {can('resource:write') && <GoipRebootButton resourceId={data.id} name={data.name} onDone={reload} />}
            </>
          )}
          {(isYeastar(data) || isDinstar(data)) && (
            <Link className="btn" to={`/resources/${data.id}/appliance-lines`}>
              <Signal size={15} /> Lines
            </Link>
          )}
          <WatchToggle scope="resource" id={data.id} name={data.name} />
          <button className="btn" onClick={checkNow} disabled={checking || !can('resource:write')}>
            <RefreshCw size={15} className={checking ? 'spin' : undefined} /> Check now
          </button>
          {can('resource:write') && (
            <>
              <button className="btn btn-icon" onClick={() => setConvertOpen(true)} title="Convert type" aria-label="Convert type">
                <Shuffle size={15} />
              </button>
              <button className="btn btn-icon" onClick={() => setEditOpen(true)} title="Settings" aria-label="Settings">
                <Settings2 size={15} />
              </button>
              <button className="btn btn-icon" onClick={remove} title="Delete" aria-label="Delete">
                <Trash2 size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="row" style={{ marginBottom: 14, gap: 4, flexWrap: 'wrap' }}>
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button key={t.id} className={`btn btn-sm${tab === t.id ? ' btn-primary' : ''}`} onClick={() => setTab(t.id)}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
      </div>

      {tab === 'dashboard' && (
        <>
        <SystemResources resource={data} metrics={data.metrics} hours={range} onChanged={reload} />

        {(data.kind === 'linux-server' || data.kind === 'voip-server') && (
          <ResourceSystemServicesCard resource={data} pluginId={data.pluginId} />
        )}

        {data.statusMessage && (
          <div
            className="card"
            style={{
              padding: '10px 14px',
              marginBottom: 14,
              fontSize: 13,
              borderLeft: `3px solid ${data.health === 'down' ? 'var(--itr-critical)' : data.health === 'degraded' ? 'var(--itr-warning)' : 'var(--itr-up)'}`,
            }}
          >
            <NoticeList resource={data} onChanged={reload} />
          </div>
        )}

        <div className="stat-strip">
          <div className="stat-chip">
            <div className="stat-chip-label">Uptime 24h</div>
            <div className="stat-chip-value" style={{ color: data.uptime24h < 99 ? 'var(--itr-warning)' : undefined }}>
              {data.uptime24h.toFixed(2)}%
            </div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-label">Uptime 30d</div>
            <div className="stat-chip-value" style={{ color: data.uptime30d < 99 ? 'var(--itr-warning)' : undefined }}>
              {data.uptime30d.toFixed(2)}%
            </div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-label">Open alerts</div>
            <div className="stat-chip-value" style={{ color: data.openAlerts.length ? 'var(--itr-critical)' : undefined }}>
              {data.openAlerts.length}
            </div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-label">Last check</div>
            <div className="stat-chip-value" style={{ fontSize: 15, fontWeight: 560 }}>
              {data.lastCheckedAt ? formatRelative(data.lastCheckedAt) : 'never'}
            </div>
          </div>
        </div>

        {data.openAlerts.length > 0 && (
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-header">
              <Activity size={14} style={{ color: 'var(--itr-critical)' }} />
              <span className="card-title">Open alerts</span>
            </div>
            <div style={{ padding: '4px 0' }}>
              {data.openAlerts.slice(0, 5).map((alert) => (
                <Link
                  key={alert.id}
                  to={`/alerts/${alert.id}`}
                  className="row"
                  style={{ padding: '9px 16px', gap: 10, color: 'inherit', textDecoration: 'none' }}
                >
                  <Badge intent={alert.severity === 'critical' ? 'bad' : 'warn'}>{alert.severity}</Badge>
                  <span className="grow truncate">{alert.title}</span>
                  <span className="subtle" style={{ fontSize: 12 }}>
                    {formatRelative(alert.lastSeenAt)}
                  </span>
                </Link>
              ))}
              {data.openAlerts.length > 5 && (
                <Link to="/alerts" className="row" style={{ padding: '9px 16px', fontSize: 12 }}>
                  View all {data.openAlerts.length} open alerts
                </Link>
              )}
            </div>
          </div>
        )}

        {data.metrics.length > 0 && (
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-header">
              <span className="card-title">Metrics</span>
              <span className="grow" />
              <div className="row" style={{ gap: 3 }}>
                {[1, 6, 24, 168].map((hours) => (
                  <button
                    key={hours}
                    className={`btn btn-sm${range === hours ? ' btn-primary' : ''}`}
                    onClick={() => setRange(hours)}
                  >
                    {hours < 24 ? `${hours}h` : hours === 24 ? '1d' : '7d'}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
              {data.metrics
                .filter((m) => !(VITAL_METRICS as readonly string[]).includes(m))
                .slice(0, 8)
                .map((metric) => (
                <MetricChart key={metric} resourceId={data.id} metric={metric} hours={range} />
              ))}
            </div>
          </div>
        )}

        <AiChatPanel subjectType="resource" subjectId={data.id} />
        </>
      )}

      {tab === 'security' && <ResourceSecurityCard resource={data} />}

      {/* What is this thing running, and what does it expose. */}
      {tab === 'services' && <ResourceServicesCard resource={data} />}

      {/* Reachability and the tools: when a device is misbehaving, "can I get
          in" is the next question after "what is it doing". */}
      {tab === 'access' && (
        <>
          {(data.kind === 'ip-camera' || data.kind === 'dvr-nvr') && <ResourceVideoCard resource={data} />}
          <ResourceAccessCard resource={data} onConfigured={reload} />
        </>
      )}
      {tab === 'diagnostics' && <ResourceDiagnosticsCard resource={data} />}
      {tab === 'discovery' && <ResourceDiscoveryCard resource={data} onAdded={reload} />}
      {tab === 'config' && <ResourceConfigCard resource={data} />}
      {tab === 'users' && <ResourceMikrotikUsersCard resource={data} />}
      {tab === 'torch' && <ResourceTorchCard resource={data} />}

      {tab === 'backups' && <ResourceBackupsCard resource={data} />}

      {tab === 'details' && (
        <>
          <ResourceVpnCard resource={data} onDiscovered={reload} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
          <AttributesCard attributes={data.attributes} />
          <AddressesCard address={data.address} secondaryAddresses={data.secondaryAddresses} macAddresses={data.macAddresses} />
          {data.children.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Child resources</span>
              </div>
              <div style={{ padding: '4px 0' }}>
                {data.children.map((child) => (
                  <Link
                    key={child.id}
                    to={`/resources/${child.id}`}
                    className="row"
                    style={{ padding: '8px 16px', gap: 10, color: 'inherit', textDecoration: 'none' }}
                  >
                    <ResourceHealth resource={child} label={false} />
                    <span className="grow truncate">{child.name}</span>
                    <Badge>{child.kind}</Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        </>
      )}

      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title={`Configure ${data.name}`}>
        <EditResourceForm
          resource={data}
          onSaved={() => {
            setEditOpen(false);
            reload();
            toast('Saved', 'good');
          }}
        />
      </Drawer>

      <Drawer open={convertOpen} onClose={() => setConvertOpen(false)} title={`Convert type — ${data.name}`}>
        <ConvertTypeForm
          resource={data}
          onSaved={() => {
            setConvertOpen(false);
            reload();
            toast('Resource converted — health resets until the next check', 'good');
          }}
        />
      </Drawer>
    </div>
  );
}

function MetricChart({ resourceId, metric, hours }: { resourceId: string; metric: string; hours: number }) {
  const { data } = useFetch(
    () => {
      const to = Date.now();
      const from = to - hours * 3_600_000;
      return api.get<Array<{ points: Array<[number, number | null]>; unit: string; labels: Record<string, string> }>>(
        '/api/metrics/query',
        {
          resourceId,
          metric,
          from,
          to,
          // Aim for ~120 points regardless of range: enough shape, no more data
          // than a chart that wide can actually show.
          stepSec: Math.max(60, Math.floor((hours * 3600) / 120)),
          agg: 'avg',
        },
      );
    },
    [resourceId, metric, hours],
    { refreshMs: 60_000 },
  );

  const series = (data ?? []).map((s, i) => ({
    name: Object.values(s.labels).join(' ') || metric,
    points: s.points,
  }));

  return (
    <div>
      <div className="row" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 590, color: 'var(--itr-text-muted)' }}>{metric}</span>
        <span className="grow" />
        <span className="subtle nums" style={{ fontSize: 12 }}>
          {formatMetric(lastValue(series), data?.[0]?.unit)}
        </span>
      </div>
      <div style={{ height: 150 }}>
        {series.length > 0 ? (
          <TimeseriesChart series={series} unit={data?.[0]?.unit} height={150} />
        ) : (
          <div className="subtle" style={{ fontSize: 12, paddingTop: 20 }}>
            No samples in this window.
          </div>
        )}
      </div>
    </div>
  );
}

function lastValue(series: Array<{ points: Array<[number, number | null]> }>): number | null {
  const points = series[0]?.points ?? [];
  for (let i = points.length - 1; i >= 0; i--) {
    const value = points[i]?.[1];
    if (value !== null && value !== undefined) return value;
  }
  return null;
}

function AttributesCard({ attributes }: { attributes: Record<string, unknown> }) {
  const entries = Object.entries(attributes).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object');
  if (entries.length === 0) return null;

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Details</span>
      </div>
      <div style={{ padding: '4px 0' }}>
        {entries.map(([key, value]) => (
          <div key={key} className="row" style={{ padding: '7px 16px', gap: 12, fontSize: 12.5 }}>
            <span className="subtle" style={{ minWidth: 130 }}>
              {humanise(key)}
            </span>
            <span className="grow truncate" style={{ textAlign: 'right' }}>
              {String(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface SavedCred {
  id: string;
  username: string | null;
  type: string;
  hasSecret: boolean;
}

/**
 * Backups scoped to this one resource: plan(s) with full manipulate
 * (edit/enable/disable/delete), a one-click way to start protecting a
 * resource that has none yet, and recent runs with restore — the same
 * capabilities as the global Backups page, so an operator working a single
 * resource never has to leave it. Reuses that page's `PlanForm` and
 * `RestoreDialog` rather than a second copy of either flow.
 */
function ResourceBackupsCard({ resource }: { resource: Resource }) {
  const { can } = useAuth();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanRow | null>(null);
  const [restoringRun, setRestoringRun] = useState<RunRow | null>(null);
  const [logRun, setLogRun] = useState<RunRow | null>(null);

  const plans = useFetch(() => api.get<PlanRow[]>('/api/plugins/backups/plans', { resourceId: resource.id }), [resource.id]);
  const runs = useFetch(() => api.get<RunRow[]>('/api/plugins/backups/runs', { resourceId: resource.id }), [resource.id]);
  const { data: targets } = useFetch(() => api.get<StorageTarget[]>('/api/plugins/backups/targets'), []);

  // Neither fetch polls, so without this a run started here stays "running"
  // until the page is reloaded.
  useRealtimeEvent(['backups'], (event) => {
    if ((event.type === 'backup.started' || event.type === 'backup.finished') && event.resourceId === resource.id) {
      plans.reload();
      runs.reload();
    }
  });

  const runNow = async (planId: string, name: string) => {
    try {
      await api.post(`/api/plugins/backups/plans/${planId}/run`);
      toast(`Backup "${name}" started`, 'good');
      setTimeout(() => {
        plans.reload();
        runs.reload();
      }, 1500);
    } catch (err) {
      toast('Could not start the backup', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const toggleEnabled = async (plan: PlanRow) => {
    await api.post('/api/plugins/backups/plans', { ...plan, enabled: !plan.enabled });
    plans.reload();
    toast(plan.enabled ? 'Backup plan disabled' : 'Backup plan enabled', 'good');
  };

  const deletePlan = async (plan: PlanRow) => {
    if (!confirm(`Delete "${plan.name}"? Existing archives are left in place.`)) return;
    await api.delete(`/api/plugins/backups/plans/${plan.id}`);
    toast('Backup plan deleted', 'good');
    plans.reload();
  };

  const host = accessHostFor(resource);
  const canWrite = can('backup:write');
  // Config backup means either SSH (a shell to run an export command in) or
  // one of the handful of HTTP vendors ProtectResourceForm actually knows how
  // to talk to (Yeastar, Dinstar). A camera/DVR/NVR is neither — it has no
  // SSH server and its config export CGI (if it even has one) isn't
  // implemented here — so the button would only ever set up a plan that fails
  // every run trying to SSH into a device that doesn't speak SSH.
  const noBackupStory = isCameraResource(resource);
  const canProtect = Boolean(host) && canWrite && !noBackupStory;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <DatabaseBackup size={14} />
        <span className="card-title">Backups</span>
        <span className="grow" />
        {canProtect && (
          <button className="btn btn-sm" onClick={() => setCreating(true)}>
            <Plus size={12} /> {plans.data && plans.data.length > 0 ? 'Add plan' : 'Protect this resource'}
          </button>
        )}
      </div>

      {!plans.data || plans.data.length === 0 ? (
        <div className="card-body subtle" style={{ fontSize: 12.5 }}>
          {noBackupStory
            ? "Config backup isn't supported for cameras, DVRs or NVRs — there's no SSH shell to export from, and no vendor-neutral config CGI to back up over HTTP."
            : host
              ? 'No backup plan yet.'
              : 'This resource has no address configured, so there is nothing to reach for a backup.'}
        </div>
      ) : (
        <div style={{ padding: '4px 0' }}>
          {plans.data.map((plan) => (
            <div key={plan.id} className="row" style={{ padding: '9px 16px', gap: 10, fontSize: 12.5 }}>
              <span className="grow truncate" style={{ fontWeight: 550 }}>
                {plan.name}
              </span>
              {!plan.enabled && (
                <span className="badge" style={{ height: 17, fontSize: 10.5 }}>
                  disabled
                </span>
              )}
              {plan.lastStatus && <Badge intent={autoIntent(plan.lastStatus)}>{plan.lastStatus}</Badge>}
              <span className="subtle nums" style={{ minWidth: 90, textAlign: 'right' }}>
                {plan.lastSuccessAt ? formatRelative(plan.lastSuccessAt) : 'never run'}
              </span>
              <span className="subtle nums" style={{ minWidth: 70, textAlign: 'right' }}>
                {plan.lastSize ? formatBytes(plan.lastSize) : '—'}
              </span>
              {can('backup:run') && (
                <button className="btn btn-sm btn-icon" onClick={() => runNow(plan.id, plan.name)} title="Back up now" aria-label="Back up now">
                  <Play size={12} />
                </button>
              )}
              {canWrite && (
                <>
                  <button
                    className="btn btn-sm btn-icon"
                    onClick={() => void toggleEnabled(plan)}
                    title={plan.enabled ? 'Disable' : 'Enable'}
                    aria-label={plan.enabled ? 'Disable' : 'Enable'}
                  >
                    {plan.enabled ? <PowerOff size={12} /> : <Power size={12} />}
                  </button>
                  <button className="btn btn-sm btn-icon" onClick={() => setEditingPlan(plan)} title="Edit plan" aria-label="Edit plan">
                    <Pencil size={12} />
                  </button>
                  <button className="btn btn-sm btn-icon" onClick={() => void deletePlan(plan)} title="Delete plan" aria-label="Delete plan">
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {(runs.data ?? []).length > 0 && (
        <>
          <div className="card-header" style={{ borderTop: 'var(--itr-border-w) solid var(--itr-border)' }}>
            <span className="card-title" style={{ fontSize: 12 }}>
              Recent backups
            </span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {(runs.data ?? []).slice(0, 8).map((run) => (
              <div key={run.id} className="row" style={{ padding: '8px 16px', gap: 10, fontSize: 12.5 }}>
                <Badge intent={autoIntent(run.state)}>{run.state}</Badge>
                <span className="subtle truncate grow" style={{ fontSize: 12 }}>
                  {formatRelative(run.startedAt)}
                </span>
                <span className="subtle nums" style={{ minWidth: 60, textAlign: 'right' }}>
                  {run.bytesWritten ? formatBytes(run.bytesWritten) : '—'}
                </span>
                <RunLogButton run={run} onOpen={setLogRun} />
                {run.state === 'success' && can('backup:restore') && (
                  <>
                    <DownloadRunButton run={run} />
                    <button className="btn btn-sm btn-icon" onClick={() => setRestoringRun(run)} title="Restore" aria-label="Restore">
                      <RotateCcw size={12} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <RunLogDrawer run={logRun} onClose={() => setLogRun(null)} />

      <Drawer open={creating} onClose={() => setCreating(false)} title={`Protect ${resource.name}`}>
        <ProtectResourceForm
          resource={resource}
          onSaved={() => {
            setCreating(false);
            plans.reload();
            toast('Backup plan created', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(editingPlan)} onClose={() => setEditingPlan(null)} title={`Edit — ${editingPlan?.name ?? ''}`}>
        {editingPlan && (
          <PlanForm
            targets={targets ?? []}
            plan={editingPlan}
            resource={resource}
            onSaved={() => {
              setEditingPlan(null);
              plans.reload();
              toast('Backup plan updated', 'good');
            }}
          />
        )}
      </Drawer>

      <Drawer open={Boolean(restoringRun)} onClose={() => setRestoringRun(null)} title={`Restore — ${restoringRun?.planName ?? ''}`}>
        {restoringRun && (
          <RestoreDialog
            run={restoringRun}
            onDone={() => {
              setRestoringRun(null);
              runs.reload();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function ProtectResourceForm({ resource, onSaved }: { resource: Resource; onSaved: () => void }) {
  const toast = useToast();
  const [targetId, setTargetId] = useState('');
  const [vaultEntryId, setVaultEntryId] = useState('');
  const [username, setUsername] = useState(accessUsername(resource.config) ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: targets } = useFetch(() => api.get<Array<{ id: string; name: string; type: string }>>('/api/plugins/backups/targets'), []);
  const { data: saved } = useFetch(() => api.get<SavedCred[]>('/api/vault/entries', { resourceId: resource.id }), [resource.id]);
  const credentialEntries = (saved ?? []).filter((e) => e.type === 'password' && e.hasSecret && e.username);

  const host = accessHostFor(resource);
  const mikrotik = isMikrotik(resource);
  // A FreePBX box is a Linux server: its backup is a database dump plus
  // /etc/asterisk, done by the runner, not a CLI command.
  // MagnusBilling is Asterisk too, but its state is the mbilling database —
  // checked first so it doesn't get a FreePBX dump.
  const magnus = !mikrotik && isMagnusBilling(resource);
  // The magnusbilling kind carries its own SSH login, which the runner reads
  // at run time; any other resource marked as MagnusBilling asks here.
  const resourceLogin = magnus && resource.kind === 'magnusbilling' && Boolean(resource.config.sshUsername) &&
    Boolean(resource.config.sshPassword || resource.config.sshPrivateKey);
  const freepbx = !mikrotik && !magnus && isAsterisk(resource);
  const yeastar = isYeastar(resource);
  const dinstar = isDinstar(resource);
  const httpVendor = yeastar ? 'yeastar' : dinstar ? 'dinstar' : null;
  const sshPort = Number(resource.config.sshPort ?? 22);

  const [protocol, setProtocol] = useState<'http' | 'https'>('https');
  const [httpPort, setHttpPort] = useState(protocol === 'https' ? 443 : 80);
  const [backupPath, setBackupPath] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetId) return;
    if (httpVendor && !backupPath.trim()) return;
    setBusy(true);
    try {
      await api.post('/api/plugins/backups/plans', {
        name: magnus ? `${resource.name} system` : `${resource.name} config`,
        resourceId: resource.id,
        sourceType: 'config',
        targetId,
        scheduleCron: '0 2 * * *',
        sourceConfig: httpVendor
          ? {
              host,
              protocol,
              port: httpPort,
              backupPath: backupPath.trim(),
              username: vaultEntryId ? undefined : username,
              password: vaultEntryId ? undefined : password || undefined,
              vaultEntryId: vaultEntryId || undefined,
              vendor: httpVendor,
            }
          : resourceLogin
            ? { host, port: sshPort, vendor: 'magnusbilling', credentials: 'resource' }
            : {
                host,
                port: sshPort,
                username: vaultEntryId ? undefined : username,
                password: vaultEntryId ? undefined : password || undefined,
                vaultEntryId: vaultEntryId || undefined,
                vendor: mikrotik ? 'mikrotik' : magnus ? 'magnusbilling' : freepbx ? 'freepbx' : 'generic',
                command: mikrotik ? '/export' : magnus || freepbx ? undefined : 'show running-config',
              },
      });
      onSaved();
    } catch (err) {
      toast('Could not create the plan', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {httpVendor ? (
        <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          Creates a daily config backup at 02:00, over HTTP(S) to <span className="mono">{host}</span> —{' '}
          {httpVendor === 'yeastar' ? 'Yeastar' : 'Dinstar'} appliances are managed through their web UI, not SSH.
          Fine-tune retention, compression and schedule afterwards from the Backups page.
        </p>
      ) : (
        <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          Creates a daily config backup at 02:00, over SSH to <span className="mono">{host}</span>. Fine-tune retention,
          compression and schedule afterwards from the Backups page.
        </p>
      )}

      {httpVendor && (
        <>
          <div className="row" style={{ gap: 8 }}>
            <div className="field" style={{ width: 110 }}>
              <label className="field-label">Protocol</label>
              <select
                className="select"
                value={protocol}
                onChange={(e) => {
                  const next = e.target.value as 'http' | 'https';
                  setProtocol(next);
                  setHttpPort(next === 'https' ? 443 : 80);
                }}
              >
                <option value="https">HTTPS</option>
                <option value="http">HTTP</option>
              </select>
            </div>
            <div className="field" style={{ width: 100 }}>
              <label className="field-label">Port</label>
              <input
                className="input"
                type="number"
                value={httpPort}
                onChange={(e) => setHttpPort(Number(e.target.value) || 0)}
              />
            </div>
            <div className="field grow">
              <label className="field-label">Backup path</label>
              <input
                className="input mono"
                placeholder="/cgi-bin/backup.cgi"
                value={backupPath}
                onChange={(e) => setBackupPath(e.target.value)}
                required
              />
            </div>
          </div>
          <p className="subtle" style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
            Open the device's web UI, open your browser's Network tab, click its "Backup"/"Export config" button, and
            copy the request path it makes (everything after the host:port) into the field above. Each run tries the{' '}
            {httpVendor === 'yeastar' ? 'Yeastar' : 'Dinstar'} factory-default login first, falling back to the
            credential below only if the device has been re-secured.
          </p>
        </>
      )}

      <div className="field">
        <label className="field-label">Storage target</label>
        <select className="select" required value={targetId} onChange={(e) => setTargetId(e.target.value)}>
          <option value="">Select…</option>
          {(targets ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.type})
            </option>
          ))}
        </select>
        {(targets ?? []).length === 0 && (
          <span className="field-help">No storage target exists yet — add one from the Backups page first.</span>
        )}
      </div>

      {resourceLogin && (
        <p className="subtle" style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
          Backs up the mbilling database, /etc/asterisk and the MagnusBilling config, logging in with the SSH login set on
          this resource — change it there and the next backup uses the new one.
        </p>
      )}

      {!resourceLogin && credentialEntries.length > 0 && (
        <div className="field">
          <label className="field-label">Credential</label>
          <select className="select" value={vaultEntryId} onChange={(e) => setVaultEntryId(e.target.value)}>
            <option value="">Enter manually…</option>
            {credentialEntries.map((c) => (
              <option key={c.id} value={c.id}>
                Saved: {c.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {!resourceLogin && !vaultEntryId && (
        <div className="row" style={{ gap: 8 }}>
          <div className="field grow">
            <label className="field-label">Username</label>
            <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
          </div>
          <div className="field grow">
            <label className="field-label">Password</label>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
          </div>
        </div>
      )}

      <button className="btn btn-primary" type="submit" disabled={busy || !targetId} style={{ width: '100%' }}>
        {busy ? 'Creating…' : 'Create plan'}
      </button>
    </form>
  );
}

function AddressesCard({
  address,
  secondaryAddresses,
  macAddresses,
}: {
  address: string | null;
  secondaryAddresses: AddressEntry[];
  macAddresses: AddressEntry[];
}) {
  if (!address && secondaryAddresses.length === 0 && macAddresses.length === 0) return null;

  return (
    <div className="card">
      <div className="card-header">
        <Network size={14} />
        <span className="card-title">Addresses</span>
      </div>
      <div style={{ padding: '4px 0' }}>
        {address && (
          <div className="row" style={{ padding: '7px 16px', gap: 12, fontSize: 12.5 }}>
            <span className="subtle" style={{ minWidth: 130 }}>
              Primary
            </span>
            <span className="grow truncate mono" style={{ textAlign: 'right' }}>
              {address}
            </span>
          </div>
        )}
        {secondaryAddresses.map((entry, i) => (
          <div key={`addr-${i}`} className="row" style={{ padding: '7px 16px', gap: 12, fontSize: 12.5 }}>
            <span className="subtle" style={{ minWidth: 130 }}>
              {entry.label || 'Address'}
            </span>
            <span className="grow truncate mono" style={{ textAlign: 'right' }}>
              {entry.value}
            </span>
          </div>
        ))}
        {macAddresses.map((entry, i) => {
          const vendor = isValidMac(entry.value) ? macVendor(entry.value) : null;
          return (
            <div key={`mac-${i}`} className="row" style={{ padding: '7px 16px', gap: 12, fontSize: 12.5 }}>
              <span className="subtle" style={{ minWidth: 130 }}>
                {entry.label || 'MAC'}
              </span>
              <span className="grow truncate" style={{ textAlign: 'right' }}>
                <span className="mono">{entry.value}</span>
                {vendor && (
                  <span className="subtle" style={{ marginLeft: 6 }}>
                    · {vendor}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Forms                                                              */
/* ================================================================== */

export function AddResourceForm({
  onCreated,
  lockPlacement,
}: {
  onCreated: () => void;
  /** When set, the resource is created directly at this client/site rather
   *  than letting the operator pick — used when the form is opened from a
   *  site's own page, where the destination is already known. */
  lockPlacement?: { customerId: string; siteId: string; label: string };
}) {
  const toast = useToast();
  const { data: kinds } = useFetch(() => api.get<ResourceKind[]>('/api/resource-kinds'), []);
  const [selected, setSelected] = useState<ResourceKind | null>(null);
  const [name, setName] = useState('');
  const [placement, setPlacement] = useState(
    lockPlacement ? { customerId: lockPlacement.customerId, siteId: lockPlacement.siteId } : { customerId: '', siteId: '' },
  );
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      await api.post('/api/resources', {
        pluginId: selected.pluginId,
        kind: selected.kind,
        name: name.trim(),
        customerId: placement.customerId || null,
        siteId: placement.siteId || null,
        config: withFieldDefaults(selected.configFields, config),
      });
      onCreated();
    } catch (err) {
      toast('Could not add the resource', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  if (!selected) {
    const grouped = new Map<string, ResourceKind[]>();
    for (const kind of kinds ?? []) {
      const list = grouped.get(kind.pluginName) ?? [];
      list.push(kind);
      grouped.set(kind.pluginName, list);
    }

    return (
      <>
        <p className="subtle" style={{ fontSize: 13, marginBottom: 16 }}>
          Pick what you want to monitor. Each type brings its own collector and metrics.
        </p>
        {[...grouped.entries()].map(([plugin, items]) => (
          <section key={plugin} style={{ marginBottom: 20 }}>
            <div className="nav-section-label" style={{ padding: '0 0 8px' }}>
              {plugin}
            </div>
            <div className="col" style={{ gap: 6 }}>
              {items.map((kind) => (
                <button
                  key={`${kind.pluginId}:${kind.kind}`}
                  className="card"
                  onClick={() => setSelected(kind)}
                  style={{ padding: '11px 13px', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 550 }}
                >
                  {kind.label}
                </button>
              ))}
            </div>
          </section>
        ))}
      </>
    );
  }

  return (
    <form onSubmit={submit}>
      <button type="button" className="btn btn-sm" onClick={() => setSelected(null)} style={{ marginBottom: 16 }}>
        ← Change type
      </button>

      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder={selected.label} autoFocus />
      </div>

      {lockPlacement ? (
        <div className="field">
          <label className="field-label">Site</label>
          <div className="input" style={{ display: 'flex', alignItems: 'center', color: 'var(--itr-text-subtle)' }}>
            {lockPlacement.label}
          </div>
        </div>
      ) : (
        <PlacementFields customerId={placement.customerId} siteId={placement.siteId} onChange={setPlacement} />
      )}

      {selected.configFields.map((field) => (
        <ConfigField key={field.key} field={field} value={config[field.key]} onChange={(v) => setConfig((c) => ({ ...c, [field.key]: v }))} />
      ))}

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Adding…' : 'Add resource'}
      </button>
    </form>
  );
}

/**
 * Repeatable label+value rows, for secondary addresses and MAC addresses —
 * identical shape, different placeholder text and validation hint.
 */
/** A bare host/IP is fine, and so is a full URL — either way this is
 *  inventory the operator reads, never an argument passed to a spawned tool. */
function isValidAddressValue(value: string): boolean {
  if (value.includes('://')) {
    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }
  return isValidTarget(value);
}

function AddressListEditor({
  rows,
  onChange,
  valuePlaceholder,
  isValidValue,
}: {
  rows: AddressEntry[];
  onChange: (rows: AddressEntry[]) => void;
  valuePlaceholder: string;
  isValidValue?: (value: string) => boolean;
}) {
  const update = (i: number, patch: Partial<AddressEntry>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="col" style={{ gap: 6, marginBottom: 8 }}>
      {rows.map((row, i) => {
        const invalid = Boolean(row.value.trim()) && isValidValue && !isValidValue(row.value.trim());
        return (
          <div key={i} className="row" style={{ gap: 6 }}>
            <input
              className="input"
              placeholder="Label"
              value={row.label}
              onChange={(e) => update(i, { label: e.target.value })}
              style={{ width: 110 }}
            />
            <input
              className="input"
              placeholder={valuePlaceholder}
              value={row.value}
              onChange={(e) => update(i, { value: e.target.value })}
              style={{ borderColor: invalid ? 'var(--itr-critical)' : undefined }}
            />
            <button type="button" className="btn btn-icon btn-sm" onClick={() => remove(i)} aria-label="Remove">
              <X size={13} />
            </button>
          </div>
        );
      })}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...rows, { label: '', value: '' }])}>
        <Plus size={12} /> Add
      </button>
    </div>
  );
}

function EditResourceForm({ resource, onSaved }: { resource: Resource; onSaved: () => void }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data: kinds } = useFetch(() => api.get<ResourceKind[]>('/api/resource-kinds'), []);
  const [name, setName] = useState(resource.name);
  const [pollInterval, setPollInterval] = useState(String(resource.pollIntervalSec ?? ''));
  const [enabled, setEnabled] = useState(resource.enabled);
  const [ignoreHealthRollup, setIgnoreHealthRollup] = useState(resource.ignoreHealthRollup ?? false);
  const [tags, setTags] = useState(resource.tags.join(', '));
  const [config, setConfig] = useState<Record<string, unknown>>(resource.config);
  const [secondaryAddresses, setSecondaryAddresses] = useState<AddressEntry[]>(resource.secondaryAddresses);
  const [macAddresses, setMacAddresses] = useState<AddressEntry[]>(resource.macAddresses);
  const [vpnLinks, setVpnLinks] = useState<VpnLink[]>(resource.vpnLinks ?? []);
  const [busy, setBusy] = useState(false);
  const [detectingMac, setDetectingMac] = useState(false);

  // Same ARP cross-reference the Access card already does passively on every
  // load (readNeighbours() vs. the resource's resolved addresses) — this just
  // makes it an explicit, on-demand action from the edit form instead of a
  // side effect the operator has to know to go look for. The server already
  // saves anything it finds (additive), so this only needs to reflect that
  // back into the still-open form rather than persist it again.
  const detectMac = async () => {
    setDetectingMac(true);
    try {
      const result = await api.get<{ discoveredMacs: AddressEntry[] }>(`/api/tools/resource/${resource.id}`);
      if (result.discoveredMacs.length > 0) {
        setMacAddresses((current) => {
          const known = new Set(current.map((m) => m.value.toUpperCase().replace(/-/g, ':')));
          const additions = result.discoveredMacs.filter((m) => !known.has(m.value.toUpperCase().replace(/-/g, ':')));
          return [...current, ...additions];
        });
        toast(
          `Found ${result.discoveredMacs.length} MAC${result.discoveredMacs.length === 1 ? '' : 's'} — remember to save`,
          'good',
        );
      } else {
        toast('No MAC found on this segment', 'info', 'The device must be on the same local network as the server — a routed or VPN-tunnelled address has no ARP entry to read.');
      }
    } catch (err) {
      toast('Could not detect MAC', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setDetectingMac(false);
    }
  };

  const definition = (kinds ?? []).find((k) => k.kind === resource.kind && k.pluginId === resource.pluginId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const clean = (rows: AddressEntry[]) => rows.filter((r) => r.value.trim()).map((r) => ({ label: r.label.trim(), value: r.value.trim() }));
      await api.patch(`/api/resources/${resource.id}`, {
        name: name.trim(),
        enabled,
        ignoreHealthRollup,
        pollIntervalSec: pollInterval ? Number(pollInterval) : null,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        secondaryAddresses: clean(secondaryAddresses),
        macAddresses: clean(macAddresses),
        vpnLinks: vpnLinks
          .filter((l) => l.peerId || l.label.trim() || l.localAddress.trim() || l.remoteAddress.trim())
          .map((l) => ({ ...l, label: l.label.trim(), localAddress: l.localAddress.trim(), remoteAddress: l.remoteAddress.trim() })),
        config: withFieldDefaults(definition?.configFields, config),
      });
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PlacementForm resource={resource} onSaved={onSaved} />

      <form onSubmit={submit}>
        <div className="nav-section-label" style={{ padding: '0 0 10px' }}>
          Connection settings
        </div>

        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>

      <div className="field">
        <label className="field-label">Tags</label>
        <input className="input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="production, hq, critical" />
        <span className="field-help">Comma separated. Alert rules can target tags.</span>
      </div>

      <div className="field">
        <label className="field-label">Other addresses</label>
        <AddressListEditor
          rows={secondaryAddresses}
          onChange={setSecondaryAddresses}
          valuePlaceholder="10.0.1.5 or https://…"
          isValidValue={isValidAddressValue}
        />
        <span className="field-help">A second NIC, a public IP, a status page — anything besides the primary address.</span>
      </div>

      <div className="field">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <label className="field-label">MAC addresses</label>
          {can('tools:run') && (
            <button type="button" className="btn btn-sm" onClick={detectMac} disabled={detectingMac}>
              {detectingMac ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null}
              {detectingMac ? 'Detecting…' : 'Get MAC'}
            </button>
          )}
        </div>
        <AddressListEditor rows={macAddresses} onChange={setMacAddresses} valuePlaceholder="AA:BB:CC:DD:EE:FF" isValidValue={isValidMac} />
        <span className="field-help">Reads this server's own ARP table for the resource's resolved addresses — only finds a MAC when the device is on the same local network as the server.</span>
      </div>

      <div className="field">
        <label className="field-label">VPN links</label>
        <VpnLinkEditor selfId={resource.id} rows={vpnLinks} onChange={setVpnLinks} />
        <span className="field-help">
          Tunnels this device holds, drawn on the network map. SSH discovery finds MikroTik PPP sessions on its own; record the
          rest here.
        </span>
      </div>

      <div className="field">
        <label className="field-label">Poll interval (seconds)</label>
        <input
          className="input"
          type="number"
          min={10}
          value={pollInterval}
          onChange={(e) => setPollInterval(e.target.value)}
          placeholder="plugin default"
        />
      </div>

      <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Monitoring enabled</span>
      </label>

      <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer', alignItems: 'flex-start' }}>
        <input type="checkbox" checked={ignoreHealthRollup} onChange={(e) => setIgnoreHealthRollup(e.target.checked)} />
        <span style={{ fontSize: 13 }}>
          Don't count toward parent, site or client status
          <span className="field-help" style={{ display: 'block' }}>
            Keeps its own status, but it no longer turns its parent resource, site or client amber or red. To make this
            resource itself read up, use Ignore on its status instead.
          </span>
        </span>
      </label>

      {definition?.configFields.map((field) => (
        <ConfigField key={field.key} field={field} value={config[field.key]} onChange={(v) => setConfig((c) => ({ ...c, [field.key]: v }))} />
      ))}

        <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </>
  );
}

/**
 * Re-points which plugin/kind owns a resource — a discovered device that
 * turned out to be a server, a workstation misidentified as a generic
 * device. This is a separate drawer rather than a field inside the settings
 * form above: the config fields for the *current* kind are irrelevant to
 * whatever kind gets picked, and closing/reopening (the settings drawer
 * unmounts on close) is what gets `EditResourceForm` to read the new kind's
 * fields rather than the ones it initialised state from at mount.
 */
function ConvertTypeForm({ resource, onSaved }: { resource: Resource; onSaved: () => void }) {
  const toast = useToast();
  const { data: kinds } = useFetch(() => api.get<ResourceKind[]>('/api/resource-kinds'), []);
  const [selected, setSelected] = useState<ResourceKind | null>(null);
  const [busy, setBusy] = useState(false);

  const currentLabel = (kinds ?? []).find((k) => k.kind === resource.kind && k.pluginId === resource.pluginId)?.label ?? resource.kind;

  const grouped = useMemo(() => {
    const map = new Map<string, ResourceKind[]>();
    for (const kind of kinds ?? []) {
      if (kind.kind === resource.kind && kind.pluginId === resource.pluginId) continue; // already this type
      const list = map.get(kind.pluginName) ?? [];
      list.push(kind);
      map.set(kind.pluginName, list);
    }
    return map;
  }, [kinds, resource.kind, resource.pluginId]);

  const convert = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/api/resources/${resource.id}`, { pluginId: selected.pluginId, kind: selected.kind });
      onSaved();
    } catch (err) {
      toast('Could not convert this resource', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
        Currently monitored as <strong>{currentLabel}</strong>. Converting changes which collector runs against this
        resource and resets its health and attributes to a clean slate — the next check re-populates them. Metrics,
        alerts, tickets and saved credentials stay attached. Review the connection settings for the new type
        afterward.
      </p>

      {[...grouped.entries()].map(([plugin, items]) => (
        <section key={plugin} style={{ marginBottom: 20 }}>
          <div className="nav-section-label" style={{ padding: '0 0 8px' }}>
            {plugin}
          </div>
          <div className="col" style={{ gap: 6 }}>
            {items.map((kind) => {
              const active = selected?.kind === kind.kind && selected?.pluginId === kind.pluginId;
              return (
                <button
                  key={`${kind.pluginId}:${kind.kind}`}
                  type="button"
                  className="card"
                  onClick={() => setSelected(kind)}
                  style={{
                    padding: '11px 13px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 550,
                    borderColor: active ? 'var(--itr-primary)' : undefined,
                    background: active ? 'color-mix(in srgb, var(--itr-primary) 8%, transparent)' : undefined,
                  }}
                >
                  {kind.label}
                </button>
              );
            })}
          </div>
        </section>
      ))}

      <button className="btn btn-primary" type="button" disabled={!selected || busy} onClick={convert} style={{ width: '100%' }}>
        {busy ? 'Converting…' : selected ? `Convert to ${selected.label}` : 'Select a type'}
      </button>
    </div>
  );
}

/**
 * Placement saves on its own, separately from the connection settings below
 * it. Several plugins mark credentials `required`, and discovered resources
 * often arrive without them — sharing one form would make "file this device
 * under a client" impossible until someone invented an AMI password.
 */
function PlacementForm({ resource, onSaved }: { resource: Resource; onSaved: () => void }) {
  const toast = useToast();
  const [placement, setPlacement] = useState({
    customerId: resource.customerId ?? '',
    siteId: resource.siteId ?? '',
  });
  const [parentId, setParentId] = useState(resource.parentId ?? '');
  const [busy, setBusy] = useState(false);

  // Fetched unfiltered — cycle detection below needs the whole parent/child
  // graph, not just this site's slice of it, even though the dropdown itself
  // only ever offers same-site candidates.
  const { data: candidates } = useFetch(() => api.get<Resource[]>('/api/resources', { limit: 500 }), []);

  // Everything already hanging off this resource, however many levels down —
  // picking any of them as its own parent would close a loop, not just a
  // direct child.
  const descendantIds = useMemo(() => {
    const childrenOf = new Map<string, string[]>();
    for (const r of candidates ?? []) {
      if (r.parentId) childrenOf.set(r.parentId, [...(childrenOf.get(r.parentId) ?? []), r.id]);
    }
    const visited = new Set<string>();
    const queue = [resource.id];
    while (queue.length > 0) {
      for (const childId of childrenOf.get(queue.shift()!) ?? []) {
        if (!visited.has(childId)) {
          visited.add(childId);
          queue.push(childId);
        }
      }
    }
    return visited;
  }, [candidates, resource.id]);

  // Same site only — the network map only ever nests within one site's
  // branch, so a cross-site parent would just orphan the render anyway. "No
  // site" is its own bucket: an internal/unsited resource can only nest
  // under another unsited one.
  const parentOptions = useMemo(
    () =>
      (candidates ?? []).filter(
        (r) => r.id !== resource.id && !descendantIds.has(r.id) && (r.siteId ?? '') === placement.siteId,
      ),
    [candidates, descendantIds, resource.id, placement.siteId],
  );

  const dirty =
    placement.customerId !== (resource.customerId ?? '') ||
    placement.siteId !== (resource.siteId ?? '') ||
    parentId !== (resource.parentId ?? '');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/api/resources/${resource.id}`, {
        customerId: placement.customerId || null,
        siteId: placement.siteId || null,
        parentId: parentId || null,
      });
      onSaved();
    } catch (err) {
      toast('Could not move the resource', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ marginBottom: 22, paddingBottom: 18, borderBottom: '1px solid var(--itr-border)' }}>
      <div className="nav-section-label" style={{ padding: '0 0 10px' }}>
        Placement
      </div>
      <PlacementFields
        customerId={placement.customerId}
        siteId={placement.siteId}
        onChange={(next) => {
          // A parent picked under the old site is meaningless once the site
          // changes — clear it rather than silently submit a stale id that
          // has since dropped out of the (now different) option list.
          if (next.siteId !== placement.siteId) setParentId('');
          setPlacement(next);
        }}
      />

      <div className="field">
        <label className="field-label">Parent resource</label>
        <select className="select" value={parentId} onChange={(e) => setParentId(e.target.value)} disabled={!placement.siteId}>
          <option value="">No parent — top level</option>
          {parentOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <span className="field-help">
          {placement.siteId
            ? "Nests this resource under another at the same site in the network map — a discovered device under its router, a switch under its firewall."
            : 'Pick a site first — a parent can only be another resource at the same site.'}
        </span>
      </div>

      <button className="btn" type="submit" disabled={busy || !dirty} style={{ width: '100%' }}>
        {busy ? 'Moving…' : dirty ? 'Save placement' : 'Placement unchanged'}
      </button>
    </form>
  );
}

/**
 * `ConfigField` displays `field.default` for a key the operator never touched,
 * but only an edit puts that key into `config` — so a field left at its shown
 * default (a GoIP's "admin" web username) was never saved, and the collector
 * read it as blank. Filling untouched keys from their defaults at submit makes
 * what gets saved match what the form showed. `== null` mirrors the `??` the
 * field uses to decide to show the default in the first place.
 */
function withFieldDefaults(
  fields: ResourceKind['configFields'] | undefined,
  config: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...config };
  for (const field of fields ?? []) {
    if (out[field.key] == null && field.default !== undefined) out[field.key] = field.default;
  }
  return out;
}

function ConfigField({
  field,
  value,
  onChange,
}: {
  field: ResourceKind['configFields'][number];
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const current = value ?? field.default ?? '';

  return (
    <div className="field">
      <label className="field-label">
        {field.label}
        {field.required && <span style={{ color: 'var(--itr-critical)' }}> *</span>}
      </label>

      {field.type === 'select' ? (
        <select className="select" value={String(current)} onChange={(e) => onChange(e.target.value)} required={field.required}>
          {!field.default && <option value="">Select…</option>}
          {field.choices?.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      ) : field.type === 'boolean' ? (
        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={Boolean(current)} onChange={(e) => onChange(e.target.checked)} />
          <span className="subtle" style={{ fontSize: 12.5 }}>
            Enabled
          </span>
        </label>
      ) : field.type === 'textarea' ? (
        <textarea
          className="textarea"
          value={String(current)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
        />
      ) : (
        <input
          className="input"
          type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
          value={String(current)}
          onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          // Stored secrets come back masked; typing replaces them, leaving them
          // alone keeps the existing value.
          autoComplete={field.secret ? 'new-password' : undefined}
        />
      )}

      {field.help && <span className="field-help">{field.help}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function titleCase(value: string): string {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanise(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).replace(/_/g, ' ');
}
